import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Clock, Download, Languages, MessageSquare, Users } from 'lucide-react'
import { Button, Card, CardBody, CardHeader, ProgressBar, SegmentedControl, SkeletonList } from '@/components/ui'
import { PageHeader, QueryBoundary, StatTile } from '@/components/domain'
import { TalkTimeChart, UsageChart } from '@/components/charts'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { LANGUAGE_LABEL } from '@/internationalization/constants'
import { formatMinutesAsHours, formatNumber, formatPercent } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import type { AnalyticsOverview } from '@/types'

const RANGES = [
  { value: '7d', key: 'analytics.range.7d' },
  { value: '30d', key: 'analytics.range.30d' },
  { value: '90d', key: 'analytics.range.90d' },
]

/**
 * Workspace analytics: volume, talk time, topics, language mix.
 *
 * Language mix is here rather than buried in settings because it is the number
 * that tells an Indian customer whether transcription is actually working on
 * their meetings — a workspace that speaks Hinglish and shows 100% English is
 * reporting a failure, not a result.
 */
export function AnalyticsPage() {
  const t = useT()
  const [range, setRange] = useState('30d')
  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.analytics.overview(range),
    queryFn: () => api.get<AnalyticsOverview>('/analytics/overview', { query: { range } }),
  })

  const totalLanguageMinutes = data?.languageMix.reduce((sum, row) => sum + row.minutes, 0) ?? 0
  const maxTopic = Math.max(1, ...(data?.topicShare.map((t) => t.count) ?? [1]))

  return (
    <div>
      <PageHeader
        title={t('analytics.title')}
        description={t('analytics.description')}
        actions={<Button leftIcon={<Download className="h-4 w-4" />}>{t('common.export')}</Button>}
        below={
          <SegmentedControl
            label={t('analytics.dateRange')}
            options={RANGES.map((r) => ({ value: r.value, label: t(r.key) }))}
            value={range}
            onChange={setRange}
          />
        }
      />

      <QueryBoundary
        isLoading={isPending}
        error={error}
        onRetry={refetch}
        skeleton={<SkeletonList rows={6} className="sb-panel p-4" />}
      >
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatTile label={t('analytics.stat.meetings')} value={formatNumber(data.totalMeetings)} icon={<MessageSquare />} />
              <StatTile label={t('analytics.stat.hoursCaptured')} value={formatMinutesAsHours(data.totalMinutes)} icon={<Clock />} />
              <StatTile
                label={t('analytics.stat.actionItems')}
                value={formatNumber(data.actionItemsCreated)}
                hint={t('analytics.completed', {
                  percent: formatPercent(data.actionItemsCompleted / Math.max(1, data.actionItemsCreated)),
                })}
                icon={<Users />}
              />
              <StatTile label={t('analytics.stat.avgLength')} value={`${data.avgMeetingMinutes}m`} icon={<Clock />} />
            </div>

            <Card>
              <CardHeader title={t('analytics.recordingVolume')} description={t('analytics.recordingVolumeDesc')} />
              <CardBody>
                <UsageChart data={data.usage} />
              </CardBody>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader
                  title={t('analytics.talkTime')}
                  description={t('analytics.talkTimeDesc')}
                />
                <CardBody>
                  <TalkTimeChart data={data.talkTime} />
                </CardBody>
              </Card>

              <div className="space-y-4">
                <Card>
                  <CardHeader title={t('analytics.topics')} description={t('analytics.topicsDesc')} />
                  <CardBody className="space-y-3">
                    {data.topicShare.map((topic) => (
                      <div key={topic.topic}>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-fg">{topic.topic}</span>
                          <span className="tabular-nums text-fg-muted">{topic.count}</span>
                        </div>
                        <ProgressBar value={topic.count / maxTopic} />
                      </div>
                    ))}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader
                    title={t('analytics.languageMix')}
                    description={t('analytics.languageMixDesc')}
                    action={<Languages aria-hidden className="h-4 w-4 text-fg-subtle" />}
                  />
                  <CardBody className="space-y-3">
                    {data.languageMix.map((row) => (
                      <div key={row.language}>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-fg">{LANGUAGE_LABEL[row.language]}</span>
                          <span className="tabular-nums text-fg-muted">
                            {formatPercent(row.minutes / Math.max(1, totalLanguageMinutes))}
                          </span>
                        </div>
                        <ProgressBar value={row.minutes / Math.max(1, totalLanguageMinutes)} tone="success" />
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </div>
            </div>
          </div>
        )}
      </QueryBoundary>
    </div>
  )
}
